/**
 * Shared configuration of the REST Query hooks
 */
export const REST_QUERY_CONFIG = {
  staleTime: 60_000, // 60 secondes
  // Floor timeout for multipart (FormData) requests: a 60 MB document cannot be sent
  // within the default API timeout (10 s) on a slow link (VPN, remote work).
  uploadTimeout: 5 * 60_000,
} as const

export type RestQueryParams = Record<string, unknown>

/**
 * Builds a URL with query parameters
 * @param url - Base URL
 * @param params - Parameters to add as a query string
 * @returns Full URL with its parameters
 */
export const buildUrlWithParams = (url: string, params?: RestQueryParams) => {
  if (!params || Object.keys(params).length === 0) return url

  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(item => {
        searchParams.append(key, String(item))
      })
    } else {
      searchParams.append(key, String(value))
    }
  })

  return `${url}?${searchParams.toString()}`
}

/**
 * Hook that builds a memoized URL with parameters
 * @param url - Base URL
 * @param params - Optional parameters
 * @returns Memoized URL with its parameters
 */
export const useUrlWithParams = (url: string, params?: RestQueryParams) =>
  buildUrlWithParams(url, params)

/**
 * Builds a TanStack Query key with optional parameters
 * @param queryKey - Base key
 * @param params - Parameters to include in the key
 * @returns Final query key
 */
export const buildQueryKey = (queryKey: unknown[], params?: RestQueryParams) =>
  params ? [...queryKey, params] : queryKey

/**
 * Hook that builds a memoized query key
 * @param queryKey - Base key
 * @param params - Optional parameters
 * @returns Memoized query key
 */
export const useQueryKey = (queryKey: unknown[], params?: RestQueryParams) =>
  buildQueryKey(queryKey, params)
